import type { HttpErrorResponse } from '@angular/common/http';

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbDate, NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, Observable, Subscription } from 'rxjs';

import { DatepickerService } from '../../services/datepicker.service';
import { DEFAULT_MODAL_OPTIONS } from '../../variables/variables';
import { AlertService } from './../../services/alert.service';
import { FiltersDataService } from './../../services/filters-data.service';
import {
  AddQuickFiltersModalComponent,
  AddQuickFiltersModalResult,
} from './add-quick-filters-modal/add-quick-filters-modal.component';
import {
  ControlType,
  DropDownItem,
  FilterItems,
  FilterItemsType,
  ICheckbox,
  IDateRangeFilterItem,
  IFilterItem,
  IFilterProvider,
  type INgSelect,
  ISelect,
  type IShowLast,
  QuickFilter,
} from './filter.interface';
import { ManageFiltersModalComponent } from './manage-filters-modal/manage-filters-modal.component';

@Component({
  selector: 'filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
})
export class FiltersComponent<T>
  implements OnInit, OnDestroy, AfterViewChecked
{
  @Input() filterItems: FilterItems;
  @Input() filterValues: T;
  @Input() itemsPerRow: number;
  @Input() cells: string;
  @Input() areas: string;
  @Input() filterProvider?: IFilterProvider<T>;

  @Input() isNewFormat = false;

  @Output() applyFilterClick = new EventEmitter<T>();

  checkboxValues: string[] = [];

  filtersForm: UntypedFormGroup;
  controlType: ControlType;

  startDate: NgbDate;
  endDate: NgbDate;
  dateFromMaxDate: NgbDateStruct;
  dateToMinDate: NgbDateStruct;

  isLoading = true;

  isDateRangeDisabled = false;
  isShowLastDisabled = false;
  claimId = 0;
  eraId = 0;
  groupEnrollmentId = 0;

  quickFilters: QuickFilter[] = [];
  defaultQuickFilter?: QuickFilter;
  someFilterValueIsSpecified: boolean;

  private defaultValues: T;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private modalService: NgbModal,
    private filterService: FiltersDataService,
    private alertService: AlertService,
    private changeDetectorRef: ChangeDetectorRef,
    private datepickerService: DatepickerService,
    private route: ActivatedRoute,
  ) {}

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    if (this.filterProvider !== undefined) {
      if (this.filterProvider.filterValues) {
        this.filterValues = this.filterProvider.filterValues;
      }
    }

    this.defaultValues = { ...this.filterValues };

    const { items } = this.filterItems;
    const requests$: Observable<any[]>[] = [];
    const requestsNames: string[] = [];

    items?.forEach((item: ICheckbox | ISelect) => {
      if (item.asyncValues) {
        requests$.push(item.asyncValues);
        requestsNames.push(item.name);
      }
    });

    this.filtersForm = this.buildFormGroup(this.filterItems);
    this.handleFiltersFormChanges();

    this.setQuickFiltersAndFindDefault();

    if (requests$.length > 0) {
      this.subscriptions.add(
        combineLatest(requests$).subscribe({
          next: (results) => {
            for (const [index, item] of requestsNames.entries()) {
              const filterElement = this.filterItems.items?.find(
                (x: ICheckbox | ISelect) => {
                  return x.name === item;
                },
              ) as ICheckbox | ISelect;
              filterElement.values = results[index] as DropDownItem[];
              this.updateForm(
                this.defaultQuickFilter
                  ? (this.defaultQuickFilter.values as T)
                  : this.filterValues,
              );
            }
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
        }),
      );
    } else {
      this.isLoading = false;
      this.updateForm(
        this.defaultQuickFilter
          ? (this.defaultQuickFilter.values as T)
          : this.filterValues,
      );
    }

    this.route.queryParams.subscribe((params) => {
      this.claimId = params.claim;
      this.eraId = params.era;
      this.groupEnrollmentId = params.groupEnrollment;
      if (
        this.claimId == 1 ||
        this.eraId == 1 ||
        this.claimId == 2 ||
        this.groupEnrollmentId == 1
      ) {
        this.onSubmit();
      }
    });
  }

  ngAfterViewChecked() {
    this.changeDetectorRef.detectChanges();
  }

  getDateRangeItem(item?: FilterItemsType): IDateRangeFilterItem | undefined {
    return item && 'dateToControlName' in item ? item : undefined;
  }

  getDateRangeCaption(
    item?: FilterItemsType,
  ): IDateRangeFilterItem | undefined {
    return item && 'captionFrom' in item ? item : undefined;
  }

  getVerticalItem(item?: FilterItemsType): IDateRangeFilterItem | undefined {
    return item && 'isVertical' in item ? item : undefined;
  }

  private buildFormGroup(filterItems: FilterItems) {
    const group: any = {};

    if (filterItems.items) {
      for (const item of filterItems.items) {
        if (
          item.type != 'dateRange' &&
          item.type != 'dateRangeCalendarInline'
        ) {
          const filterElement = item as ICheckbox | IFilterItem | ISelect;

          group[filterElement.name] = new UntypedFormControl(
            this.filterValues[filterElement.name],
            filterElement.validators,
          );
        } else {
          const filterElement = item as IDateRangeFilterItem;

          group[filterElement.dateFromControlName] = new UntypedFormControl(
            this.filterValues[filterElement.dateFromControlName],
          );

          group[filterElement.dateToControlName] = new UntypedFormControl(
            this.filterValues[filterElement.dateToControlName],
          );
        }
      }
    }

    return new UntypedFormGroup(group as Record<string, UntypedFormControl>);
  }

  private handleFiltersFormChanges() {
    this.subscriptions.add(
      this.filtersForm.valueChanges.subscribe((value: object) => {
        const keys = Object.keys(value);
        this.someFilterValueIsSpecified = keys.some((x) => {
          return !!value[x];
        });
      }),
    );
  }

  private setQuickFiltersAndFindDefault(quickFilters?: QuickFilter[]) {
    this.quickFilters = quickFilters
      ? quickFilters
      : this.filterService.getQuickFilters(this.filterItems.filtersName);
    this.defaultQuickFilter = this.quickFilters.find((x) => {
      return x.default;
    });
  }

  openManageFiltersModal() {
    const modal = this.modalService.open(ManageFiltersModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'sm',
    });
    const instance: ManageFiltersModalComponent<T> =
      modal.componentInstance as ManageFiltersModalComponent<T>;

    instance.quickFilters = [...this.quickFilters];
    instance.filtersName = this.filterItems.filtersName;
    instance.currentFiltersFormValues = this.filtersForm.value;
    instance.isAddQuickFilterEnabled = this.someFilterValueIsSpecified;

    modal.result
      .then((value: QuickFilter[]) => {
        setTimeout(() => {
          this.setQuickFiltersAndFindDefault(value);
        });
      })
      .catch((error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
      });
  }

  addQuickFilter() {
    const modal = this.modalService.open(AddQuickFiltersModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'sm',
    });

    const modalInstance =
      modal.componentInstance as AddQuickFiltersModalComponent;
    modalInstance.quickFiltersNames = this.quickFilters.map((x) => {
      return x.name;
    });

    modal.result
      .then((result) => {
        if (result) {
          const modalResult = result as AddQuickFiltersModalResult;
          const qf: QuickFilter = {
            name: modalResult.filterName,
            default: modalResult.setAsDefault,
            values: this.filtersForm.value,
          };

          this.filterService.saveQuickFilter(this.filterItems.filtersName, qf);

          setTimeout(() => {
            this.setQuickFiltersAndFindDefault();
          });
        }
      })
      .catch((error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
      });
  }

  onShowLastChange(event: any) {
    this.isDateRangeDisabled = event.target.value ? true : false;
  }

  onDateRangeChange(controlNameFrom: string, controlNameTo: string) {
    const dateFrom = this.filtersForm.get(controlNameFrom)?.value as string;
    const dateTo = this.filtersForm.get(controlNameTo)?.value as string;

    this.isShowLastDisabled = !dateFrom && !dateTo ? false : true;

    this.dateFromMaxDate = this.datepickerService.toNgbDate(
      this.datepickerService.fromISOString(dateTo),
    );

    this.dateToMinDate = this.datepickerService.toNgbDate(
      this.datepickerService.fromISOString(dateFrom),
    );
  }

  onSubmit() {
    const form = this.filtersForm.value;

    if (this.isNewFormat) {
      let obj = {};

      if (this.filterItems.items) {
        for (const item of this.filterItems.items) {
          const filterElement = item as ICheckbox | IFilterItem | ISelect;

          const itemName = filterElement.name;
          const operator = filterElement.queryOperator;

          obj = {
            ...obj,
            [itemName]: operator
              ? { [operator]: form[itemName] }
              : form[itemName],
          };
        }
      }

      this.doApplyFilterClick({
        ...form,
        ...obj,
      } as T);

      return;
    }

    if (this.filterProvider) {
      this.doApplyFilterClick(form as T);
    } else {
      this.applyFilterClick.emit(form as T);
    }
  }

  resetFilters() {
    this.updateForm(this.defaultValues);
  }

  private updateForm(values: T) {
    if (!this.filtersForm.value) {
      return;
    }

    for (const key of Object.keys(this.filtersForm.value as object)) {
      const itemName = this.filterItems.items?.find((x) => {
        return x.name === key;
      });

      if (itemName) {
        const opertor = itemName.queryOperator;
        const value =
          opertor && values[key] ? values[key][opertor] : values[key];
        this.filtersForm.controls[key].setValue(value ? value : '');
      } else {
        this.filtersForm.controls[key].setValue(values[key]);
      }
    }
  }

  setDefaultQuickFilter() {
    const quickFilter = this.defaultQuickFilter?.values as T;
    if (quickFilter) {
      this.updateForm(quickFilter);
    }
  }

  setQuickFilter(name: string) {
    const qf = this.quickFilters.find((x) => {
      return x.name == name;
    });

    if (qf) {
      this.updateForm(qf.values as T);
    } else {
      this.alertService.error(
        'Service error',
        `Could not load quick filter values`,
      );
    }
  }

  //return value of checkbox insted of true or false
  onCheckChange(event: any, name: string) {
    const value = event.target.value as string;
    const control = this.filtersForm.get(name);

    if (event.target.checked) {
      this.checkboxValues.push(value);
      if (control) {
        control.setValue(this.checkboxValues);
      }
    } else {
      this.checkboxValues = this.checkboxValues.filter((x) => {
        return x != value;
      });
      if (control) {
        control.setValue(
          this.checkboxValues.length > 0 ? this.checkboxValues : null,
        );
      }
    }
  }

  onRadioClick(fieldName: string, value: any) {
    const field = this.filtersForm.get(fieldName);
    if (field) {
      const fieldValue = field.value;
      if (fieldValue === value) {
        field.setValue('');
      }
    }
  }

  private doApplyFilterClick(filterValues: T) {
    this.filterProvider?.applyFilter(filterValues);
  }

  isFilterItemOfType(
    item: FilterItemsType,
    cType: ControlType,
  ): FilterItemsType | undefined {
    return item.type === cType ? item : undefined;
  }

  isShowlastItem = (item: FilterItemsType) => {
    const result = this.isFilterItemOfType(item, 'showlast');

    if (result) {
      return result as IShowLast;
    }

    return undefined;
  };
  isNgSelectItem = (item: FilterItemsType) => {
    const result = this.isFilterItemOfType(item, 'ngselect');

    if (result) {
      return result as INgSelect;
    }

    return undefined;
  };
  isDateRangeItem = (item: FilterItemsType) => {
    const result = this.isFilterItemOfType(item, 'dateRange');

    if (result) {
      return result as IDateRangeFilterItem;
    }

    return undefined;
  };
  isDateRangeCalendarInlineItem = (item: FilterItemsType) => {
    return this.isFilterItemOfType(item, 'dateRangeCalendarInline');
  };
  isRadioItem = (item: FilterItemsType) => {
    const result = this.isFilterItemOfType(item, 'radio');

    if (result) {
      return result as ICheckbox;
    }

    return undefined;
  };
  isCheckboxItem = (item: FilterItemsType) => {
    const result = this.isFilterItemOfType(item, 'checkbox');

    if (result) {
      return result as ICheckbox;
    }

    return undefined;
  };
  isHorizontalSwitchItem = (item: FilterItemsType) => {
    const result = this.isFilterItemOfType(item, 'horizontalSwitch');
    if (result) {
      return result as ICheckbox;
    }

    return undefined;
  };
}
